import { Component, computed, OnInit, signal } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import { LIST_PAGE_SIZE_OPTIONS, paginatorConfig } from "@app/app.const";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { IApiResponse } from "@models/api-response.model";
import {
  IPaginatedCategoryItem,
  IPaginatedCategoryList,
  IProductListApiResponse,
  IProductResponseDto,
} from "@models/product-list.model";
import { AppStateService, RequestService } from "@services/index";
import { ProductService } from "@services/product/product.service";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
  RefreshButtonComponent,
} from "../../../components";

@Component({
  selector: "fh-product-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.css"],
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    FormAutocompleteComponent,
    FilterBarComponent,
    DataTableComponent,
    ReactiveFormsModule,
    RefreshButtonComponent,
  ],
})
export class ProductListPageComponent implements OnInit {
  /** Bound wrapper for the refresh button — see `fetchData()`. */
  refreshData = (): void => this.fetchData();
  // State signals
  iso2 = signal<string | null>(null);
  vendorIso2 = signal<string | null>(null);
  merchantId = signal<number | null>(null);
  categoryId = signal<string | null>(null);
  subcategoryId = signal<string | null>(null);
  subsubcategoryId = signal<string | null>(null);
  searchText = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("id");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);
  success_msg = signal<string>("");
  error_msg = signal<string>("");

  // Data signals
  productList = signal<IProductResponseDto[]>([]);
  total = signal<number>(0);
  merchantList = signal<{ quantity: number; vendor_id: number; name: string }[]>([]);
  categoryList = signal<IPaginatedCategoryItem[]>([]);
  subcategoryList = signal<{ id: number; category_name: string; parent_id: number }[]>([]);
  /** The selected subcategory's subsubcategories; empty until a subcategory is chosen. */
  subsubcategoryList = signal<{ id: number; category_name: string; parent_id: number }[]>([]);
  expiredCount = signal<number>(0);
  totalProducts = signal<number>(0);
  totalProductsAllCategories = signal<number>(0);

  // Computed filter options
  destinationCountries = computed(() => this.appstate.destinationCountries());

  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.destinationCountries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: country.itemCount,
    }));
  });

  // Vendor (seller origin) country can be any country, not just destination-enabled ones.
  vendorCountryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.appstate.allCountries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: 0,
    }));
  });

  merchantOptions = computed<FormAutocompleteOption<number>[]>(() => {
    return this.merchantList().map((merchant) => ({
      value: merchant.vendor_id,
      label: merchant.name,
      count: merchant.quantity,
    }));
  });

  categoryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    const options = this.categoryList().map((category) => ({
      value: String(category.id),
      label: category.category_name,
      count: category.quantity,
    }));
    options.unshift({
      value: "expired",
      label: "Expired Products",
      count: this.expiredCount(),
    });
    return options;
  });

  subcategoryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.subcategoryList().map((sc) => ({
      value: String(sc.id),
      label: sc.category_name,
      count: 0,
    }));
  });

  subsubcategoryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.subsubcategoryList().map((ssc) => ({
      value: String(ssc.id),
      label: ssc.category_name,
      count: 0,
    }));
  });

  countryFilterFormControl = new FormControl<string | null>(null);

  vendorCountryFilterFormControl = new FormControl<string | null>(null);

  merchantFilterFormControl = new FormControl<number | null>(null);

  categoryFilterFormControl = new FormControl<string | null>(null);

  subcategoryFilterFormControl = new FormControl<string | null>(null);

  subsubcategoryFilterFormControl = new FormControl<string | null>(null);

  // Table configuration
  tableConfig = computed<TableConfig<IProductResponseDto>>(() => ({
    columns: this.getTableColumns(),
    data: this.productList(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: paginatorConfig.showFirstLastButtons,
    stickyHeader: true,
    highlightRowOnClick: true,
    rowClickable: false,
    detailsRoute: "/product_input/:id",
    getDetailsRouteParams: (row) => ({ id: row.id }),
  }));

  tableActions = computed<TableAction<IProductResponseDto>[]>(() => [
    {
      icon: "content_copy",
      label: "Duplicate Product",
      handler: (row, event) => this.duplicate(row.id, event),
    },
    {
      icon: "delete",
      label: "Delete Product",
      handler: (row, event) => this.delete(row.id, event),
      color: "warn",
    },
  ]);

  readonly paginatorConfig = paginatorConfig;

  constructor(
    private readonly auth: RequestService,
    private readonly productService: ProductService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly appstate: AppStateService
  ) {}

  ngOnInit(): void {
    // Subscribe to query params changes
    this.activatedRoute.queryParams.subscribe((params) => {
      this.pageIndex.set(+(params.page || 0));
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.searchText.set(params.search || "");
      this.iso2.set(params.iso2 || null);
      this.vendorIso2.set(params.vendorIso2 || null);
      this.merchantId.set(params.merchantId ? +params.merchantId : null);
      this.categoryId.set(params.categoryId || null);
      this.subcategoryId.set(params.subcategoryId || null);
      this.subsubcategoryId.set(params.subsubcategoryId || null);
      this.sortProp.set(params.sortProp || "id");
      this.sortDir.set((params.sortDir as "asc" | "desc") || "desc");

      // for the first time when this page loads we need to patch value to form control
      this.countryFilterFormControl.patchValue(this.iso2() || null);
      this.vendorCountryFilterFormControl.patchValue(this.vendorIso2() || null);
      this.merchantFilterFormControl.patchValue(this.merchantId() || null);
      this.categoryFilterFormControl.patchValue(this.categoryId() || null);
      this.subcategoryFilterFormControl.patchValue(this.subcategoryId() || null);
      this.subsubcategoryFilterFormControl.patchValue(this.subsubcategoryId() || null);

      this.fetchData();
    });
  }

  addProduct(): void {
    this.router.navigate(["/product_input/0"]);
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({
      iso2: iso2 || undefined,
      merchantId: undefined,
      categoryId: undefined,
      page: 0,
    });
  }

  onMerchantChange(merchantId: number | null): void {
    this.updateQueryParams({
      merchantId: merchantId || undefined,
      categoryId: undefined,
      page: 0,
    });
  }

  onVendorCountryChange(vendorIso2: string | null): void {
    this.updateQueryParams({
      vendorIso2: vendorIso2 || undefined,
      // The merchant must belong to the selected vendor country — reset it so a stale
      // merchant from a different country isn't kept.
      merchantId: undefined,
      page: 0,
    });
  }

  onCategoryChange(categoryId: string | null): void {
    this.updateQueryParams({
      categoryId: categoryId || undefined,
      // Subcategories depend on the selected category — reset when category changes.
      subcategoryId: undefined,
      subsubcategoryId: undefined,
      page: 0,
    });
  }

  onSubcategoryChange(subcategoryId: string | null): void {
    this.updateQueryParams({
      subcategoryId: subcategoryId || undefined,
      // Subsubcategories belong to one subcategory — reset when it changes.
      subsubcategoryId: undefined,
      page: 0,
    });
  }

  onSubsubcategoryChange(subsubcategoryId: string | null): void {
    this.updateQueryParams({
      subsubcategoryId: subsubcategoryId || undefined,
      page: 0,
    });
  }

  onSearchChange(searchText: string): void {
    this.updateQueryParams({
      search: searchText || undefined,
      page: 0,
    });
  }

  onResetFilters(): void {
    this.updateQueryParams({
      iso2: undefined,
      vendorIso2: undefined,
      merchantId: undefined,
      categoryId: undefined,
      subcategoryId: undefined,
      subsubcategoryId: undefined,
      search: undefined,
      page: 0,
      sortProp: undefined,
      sortDir: undefined,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  private fetchData(): void {
    this.isLoading.set(true);
    const iso2Param = this.iso2() || "";
    const vendorIso2Param = this.vendorIso2() || "";

    // Fetch merchants with product counts — scoped to the selected vendor (origin) country
    // so only merchants of that country appear in the Merchant dropdown.
    this.auth
      .get(
        `/api/v2/fetch-vendor-with-products-in-country?iso2=${iso2Param}&vendorIso2=${encodeURIComponent(vendorIso2Param)}`
      )
      .subscribe({
        next: (res: any) => {
          if (res.result_code === 1) {
            this.merchantList.set(res.data.vendors || []);
            this.totalProducts.set(res.data.count || 0);
            this.fetchCategories();
            this.fetchSubcategories();
            this.fetchSubsubcategories();
            this.fetchProducts();
          }
        },
        error: (error) => {
          console.error("Error fetching vendors:", error);
          this.merchantList.set([]);
          this.totalProducts.set(0);
          this.isLoading.set(false);
        },
      });
  }

  private fetchCategories(): void {
    const merchantId = this.merchantId() || 0;
    const iso2 = this.iso2() || "";

    this.auth
      .get<IApiResponse<IPaginatedCategoryList>>(
        `/api/v1/count_category_list?vendorId=${merchantId}&iso2=${iso2}`
      )
      .subscribe({
        next: (res) => {
          if (res.result_code === 1) {
            this.expiredCount.set(res.data.expired_count);
            this.totalProductsAllCategories.set(res.data.count || 0);
            this.categoryList.set(res.data.list || []);
          } else {
            this.categoryList.set([]);
            this.totalProductsAllCategories.set(0);
            this.expiredCount.set(0);
          }
        },
        error: (error) => {
          console.error("Error fetching categories:", error);
          this.categoryList.set([]);
          this.totalProductsAllCategories.set(0);
          this.expiredCount.set(0);
        },
      });
  }

  private fetchSubcategories(): void {
    // If a real (numeric) category is selected, scope subcategories to it; else load all.
    const cat = this.categoryId();
    const parentParam = cat && cat !== "all" && cat !== "expired" ? `?parentId=${cat}` : "";
    this.auth.get<IApiResponse<any[]>>(`/api/v1/subcategory_list${parentParam}`).subscribe({
      next: (res) => this.subcategoryList.set(res.result_code === 1 ? res.data || [] : []),
      error: () => this.subcategoryList.set([]),
    });
  }

  /** The selected subcategory's subsubcategories (the same endpoint, one level down). */
  private fetchSubsubcategories(): void {
    const sub = this.subcategoryId();
    if (!sub) {
      this.subsubcategoryList.set([]);
      return;
    }
    this.auth.get<IApiResponse<any[]>>(`/api/v1/subcategory_list?parentId=${sub}`).subscribe({
      next: (res) => this.subsubcategoryList.set(res.result_code === 1 ? res.data || [] : []),
      error: () => this.subsubcategoryList.set([]),
    });
  }

  private fetchProducts(): void {
    const data = {
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      sortProp: this.sortProp(),
      sortDir: this.sortDir(),
      search: this.searchText(),
      category: this.categoryId() || "all",
      subcategoryId: this.subcategoryId() ? +this.subcategoryId()! : undefined,
      subsubcategoryId: this.subsubcategoryId() ? +this.subsubcategoryId()! : undefined,
      merchant: this.merchantId() || 0,
      iso2: this.iso2() || "",
      vendorIso2: this.vendorIso2() || "",
      expired: this.categoryId() === "expired",
    };

    this.isLoading.set(true);
    this.auth.post<IProductListApiResponse>("/api/v1/product_list", data).subscribe({
      next: (res: IProductListApiResponse) => {
        if (res.result_code !== 1) {
          this.productList.set([]);
          this.total.set(0);
          this.isLoading.set(false);
          return;
        }

        const products = (res.data.list || [])
          .filter((i: any) => !!i)
          .map((d: IProductResponseDto) => ({
            ...d,
            status: d.status?.trim(),
            expiryDisplayText: d.expires_at
              ? this.productService.getExpiryDisplayText(d.expires_at).text
              : "",
            isExpired: d.expires_at
              ? this.productService.getExpiryDisplayText(d.expires_at).isExpired
              : false,
          }));

        this.productList.set(products);
        this.total.set(res.data.count || 0);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error("Error fetching products:", error);
        this.productList.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private duplicate(id: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.auth.post<IApiResponse>(`/api/v2/product/${id}/duplicate`, {}).subscribe({
      next: (res) => {
        if (res.status) {
          this.success_msg.set(res.message || "Product duplicated successfully");
          this.fetchProducts();
          setTimeout(() => this.success_msg.set(""), 7000);
        } else {
          this.error_msg.set(res.message || "Failed to duplicate the product. Please try again.");
          setTimeout(() => this.error_msg.set(""), 7000);
        }
      },
      error: (err: any) => {
        console.error("Failed to duplicate product:", err);
        this.error_msg.set(
          err?.error?.message || "Failed to duplicate the product. Please try again."
        );
        setTimeout(() => this.error_msg.set(""), 7000);
      },
    });
  }

  private delete(id: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (
      !confirm(
        "Delete this product permanently?\n\n" +
          "It will also be removed from any customer's cart and wishlist, and from search. This cannot be undone."
      )
    ) {
      return;
    }

    // Was calling /api/v1/vendor/vendor_product_list — the merchant portal's
    // endpoint, which does not exist on admin-backend, so every delete 404'd
    // silently. Errors were swallowed too: the old subscribe passed only a
    // `next` callback, so failures went nowhere and the click appeared to do
    // nothing at all.
    this.auth.delete(`/api/v2/product/${id}`).subscribe({
      next: (res: any) => {
        if (res?.status) {
          this.success_msg.set("Deleted successfully");
          this.fetchProducts();
          setTimeout(() => this.success_msg.set(""), 7000);
        } else {
          this.showDeleteError(res?.message);
        }
      },
      error: (err: any) => {
        console.error("Failed to delete product:", err);
        this.showDeleteError(err?.error?.message);
      },
    });
  }

  private showDeleteError(message?: string): void {
    this.error_msg.set(message || "Failed to delete the product. Please try again.");
    setTimeout(() => this.error_msg.set(""), 7000);
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<IProductResponseDto>[] {
    return [
      {
        key: "id",
        header: "ID",
        sortable: true,
        type: "number",
      },
      {
        key: "product_image",
        header: "Image",
        sortable: false,
        type: "image",
      },
      {
        key: "product_name",
        header: "Name",
        sortable: true,
        type: "text",
      },
      {
        key: "merchant_name",
        header: "Merchant",
        sortable: false,
      },
      {
        key: "selling_in",
        header: "Selling In",
        sortable: false,
      },
      {
        key: "qty",
        header: "Qty",
        sortable: false,
      },
      {
        key: "list_price",
        header: "Price",
        sortable: false,
        format: (value) => value || "—",
      },
      {
        key: "expires_at",
        header: "Expiry",
        sortable: false,
        format: (value) => (value ? new Date(value).toLocaleDateString("en-US") : "—"),
        cssClass: (row) =>
          row.isExpired ? "product-expiry-status--expired" : "product-expiry-status--active",
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
      },
      {
        key: "created_on",
        header: "Upload Date",
        sortable: true,
        type: "date",
        format: (value) =>
          value
            ? new Date(value).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })
            : "—",
      },
      {
        key: "updated_on",
        header: "Updated",
        sortable: true,
        type: "date",
        format: (value) =>
          value
            ? new Date(value).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })
            : "—",
      },
      {
        key: "subcategory_name",
        header: "Subcategory",
        sortable: false,
      },
    ];
  }
}
